import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, MagnifyingGlass, EnvelopeSimple, PencilSimple, Trash,
  Eye, EyeSlash, Copy, DownloadSimple,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import { itApi, EMAIL_STATUS_META } from './itApi';
import { DataTable } from '@/components/ui/DataTable';
import EmailFormDialog from './EmailFormDialog';

const StatCard = ({ label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4">
    <p className="text-xs text-zinc-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent || 'text-zinc-900'}`}>{value}</p>
    {sub && <p className="text-[11px] text-zinc-400 mt-0.5">{sub}</p>}
  </div>
);

export default function EmailManagement() {
  const { user } = useAuth();
  if (!canEditIt(user)) {
    return <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center text-sm text-zinc-500" data-testid="email-no-access">このページを利用するにはIT編集の権限が必要です。</div>;
  }
  return <EmailAdmin />;
}

function EmailAdmin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [revealed, setRevealed] = useState({});

  const load = useCallback(() => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (q.trim()) params.q = q.trim();
    itApi.emails(params).then(setItems).catch(() => {});
    itApi.emailStats().then(setStats).catch(() => {});
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const del = async (e) => {
    if (!window.confirm(`「${e.email}」を削除しますか？`)) return;
    try { await itApi.deleteEmail(e.id); toast.success('削除しました'); load(); }
    catch { toast.error('削除に失敗しました'); }
  };

  const toggleReveal = async (e) => {
    if (revealed[e.id] !== undefined) {
      setRevealed((p) => { const n = { ...p }; delete n[e.id]; return n; });
      return;
    }
    try {
      const { password } = await itApi.revealEmail(e.id);
      setRevealed((p) => ({ ...p, [e.id]: password || '' }));
    } catch { toast.error('パスワードの取得に失敗しました'); }
  };

  const copyPw = async (pw) => {
    try { await navigator.clipboard.writeText(pw); toast.success('コピーしました'); }
    catch { toast.error('コピーに失敗しました'); }
  };

  const exportCsv = async () => {
    try {
      const res = await itApi.exportEmailsCsv();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'email_accounts.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSVを書き出しました（パスワードを含みます）');
    } catch { toast.error('CSV書き出しに失敗しました'); }
  };

  return (
    <div className="space-y-5" data-testid="email-management">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">会社メール管理</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" data-testid="email-export-csv-btn" onClick={exportCsv} className="h-11 rounded-lg">
            <DownloadSimple size={18} className="mr-1.5" /> CSV書き出し
          </Button>
          <Button data-testid="email-new-btn" onClick={() => { setEditItem(null); setFormOpen(true); }} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={18} className="mr-1.5" weight="bold" /> 新規登録
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="email-stats">
          <StatCard testid="email-stat-total" label="アカウント" value={stats.total} />
          <StatCard testid="email-stat-active" label="有効" value={stats.by_status?.active ?? 0} accent="text-emerald-600" />
          <StatCard testid="email-stat-suspended" label="停止" value={stats.by_status?.suspended ?? 0} accent={stats.by_status?.suspended ? 'text-zinc-600' : 'text-zinc-900'} />
          <StatCard testid="email-stat-providers" label="プロバイダ数" value={(stats.by_provider || []).length} sub={(stats.by_provider || [])[0]?.provider || '—'} />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="email-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="メール・利用者・用途で検索" className="h-10 pl-9 rounded-lg" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="email-status-filter" className="h-10 w-36 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">状態: すべて</SelectItem>
            <SelectItem value="active">有効</SelectItem>
            <SelectItem value="suspended">停止</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        dataTestId="email-table"
        rows={items}
        rowKey={(e) => e.id}
        rowTestId={(e) => `email-row-${e.id}`}
        empty={{ icon: <EnvelopeSimple size={40} className="text-zinc-300 mx-auto mb-2" weight="duotone" />, text: 'メールアカウントがありません', testid: 'email-empty' }}
        columns={[
          { key: 'email', primary: true, header: 'メールアドレス', cell: (e) => (
            <span className="font-medium text-zinc-800">{e.email}{e.purpose && <span className="block text-xs text-zinc-400 font-normal">{e.purpose}</span>}</span>
          ) },
          { key: 'owner', header: '利用者', cell: (e) => (
            <span className="text-zinc-600">{(e.owner_names && e.owner_names.length) ? e.owner_names.join('、') : (e.owner_name || <span className="text-zinc-300">—</span>)}</span>
          ) },
          { key: 'password', header: 'パスワード', cell: (e) => {
            const pw = revealed[e.id];
            if (!e.has_password) return <span className="text-zinc-300 text-xs">未設定</span>;
            return pw !== undefined ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="font-mono text-xs bg-zinc-100 rounded px-2 py-1" data-testid={`email-pw-value-${e.id}`}>{pw || '（空）'}</span>
                {pw && <button data-testid={`email-pw-copy-${e.id}`} onClick={() => copyPw(pw)} className="text-zinc-400 hover:text-teal-700" title="コピー"><Copy size={15} /></button>}
                <button data-testid={`email-pw-hide-${e.id}`} onClick={() => toggleReveal(e)} className="text-zinc-400 hover:text-zinc-700" title="隠す"><EyeSlash size={15} /></button>
              </span>
            ) : (
              <button data-testid={`email-pw-reveal-${e.id}`} onClick={() => toggleReveal(e)} className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-teal-700">
                <Eye size={15} /> <span className="font-mono tracking-widest">••••••</span>
              </button>
            );
          } },
          { key: 'status', header: '', cell: (e) => {
            const sm = EMAIL_STATUS_META[e.status] || EMAIL_STATUS_META.active;
            return <Badge variant="outline" className={sm.cls}>{sm.label}</Badge>;
          } },
          { key: 'actions', actions: true, header: '', cell: (e) => (<>
            <Button size="sm" variant="ghost" data-testid={`email-edit-${e.id}`} onClick={() => { setEditItem(e); setFormOpen(true); }} className="h-9 px-2 text-zinc-600" title="編集"><PencilSimple size={16} /></Button>
            <Button size="sm" variant="ghost" data-testid={`email-delete-${e.id}`} onClick={() => del(e)} className="h-9 px-2 text-red-600 hover:bg-red-50" title="削除"><Trash size={16} /></Button>
          </>) },
        ]}
      />

      <EmailFormDialog open={formOpen} onClose={() => setFormOpen(false)} account={editItem} onSaved={load} />
    </div>
  );
}
